import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  RiArrowRightLine, RiStarFill, RiCalendarCheckLine, RiTeamLine,
  RiHeartLine, RiComputerLine, RiGlobalLine, RiAwardLine, RiRocketLine,
  RiMicLine, RiLightbulbLine, RiShakeHandsLine, RiGroupLine,
  RiArrowLeftSLine, RiArrowRightSLine, RiMailLine, RiCheckLine,
  RiVidiconLine, RiMoneyDollarCircleLine, RiBarChartGroupedLine,
  RiStarLine, RiCalendarLine,
} from "react-icons/ri";
import { API_BASE } from "../config";
import axios from "axios";

/* ─── STATIC DATA ───────────────────────────────────────────────────────── */
const values = [
  { icon: RiLightbulbLine, title: "Academic Excellence", description: "Curating cutting-edge research and fostering scholarly dialogue that pushes boundaries." },
  { icon: RiComputerLine, title: "Tech Innovation", description: "Virtual attendance, interactive sessions, and seamless digital networking opportunities." },
  { icon: RiTeamLine, title: "Cross-Disciplinary", description: "Bringing together diverse fields to create a rich tapestry of ideas and collaboration." },
  { icon: RiHeartLine, title: "Passionate Service", description: "Meticulous planning so you focus on content, connections, and meaningful exchange." },
];

const testimonials = [
  { text: "An incredible experience that exceeded all expectations. The speakers were world-class and the networking was invaluable.", name: "John Smith", role: "CEO, TechCorp", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&q=80" },
  { text: "Three days that fundamentally changed how I lead my team. The content was exceptionally relevant.", name: "Priya Lamba", role: "MD, Vantage Capital", img: "https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=80&q=80" },
  { text: "Every year I come back because the quality is unmatched. This summit is where the decisions of tomorrow are shaped.", name: "Robert Klein", role: "Partner, Meridian Group", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&q=80" },
];

const tickerAnnouncements = [
  "🎉 Early Bird Registration Now Open — Save up to 40%!",
  "🔔 New Speaker Announced: Join us for an inspiring keynote",
  "📍 Dubai venue confirmed: Madinat Jumeirah Conference Centre",
  "🏆 Nominations open for the 2024 Global Leadership Awards",
  "🌍 30+ countries represented — Don't miss the largest summit of the year",
  "📅 Singapore Tickets Selling Fast — Only 200 seats remaining!",
];

const services = [
  { icon: RiCalendarCheckLine, title: "Conference Management", desc: "End-to-end professional conference planning and execution.", img: "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=600&q=80&auto=format&fit=crop" },
  { icon: RiMicLine, title: "Speaker Curation", desc: "Globally acclaimed keynote speakers aligned with your summit's vision.", img: "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600&q=80&auto=format&fit=crop" },
  { icon: RiGroupLine, title: "Delegate Experience", desc: "Immersive networking programs and curated one-on-one meeting opportunities.", img: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80&auto=format&fit=crop" },
  { icon: RiGlobalLine, title: "Global Outreach", desc: "Marketing targeting senior decision-makers across 30+ countries.", img: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=600&q=80&auto=format&fit=crop" },
  { icon: RiVidiconLine, title: "Virtual & Hybrid Events", desc: "Seamless streaming platforms with interactive features for remote attendees worldwide.", img: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&q=80&auto=format&fit=crop" },
  { icon: RiMoneyDollarCircleLine, title: "Sponsorship Packages", desc: "Tailored partnership opportunities with measurable ROI and brand visibility.", img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&q=80&auto=format&fit=crop" },
  { icon: RiBarChartGroupedLine, title: "Event Analytics", desc: "Real-time data insights and post-event reporting to measure success metrics.", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80&auto=format&fit=crop" },
  { icon: RiStarLine, title: "Awards & Gala Nights", desc: "Memorable evening ceremonies celebrating industry excellence and achievements.", img: "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=600&q=80&auto=format&fit=crop" },
];

const whyAttend = [
  { icon: RiMicLine, title: "50+ World-Class Speakers", desc: "Learn from CEOs, founders, and global thought leaders shaping industries." },
  { icon: RiGroupLine, title: "5,000+ Leaders Networking", desc: "Build relationships with decision-makers from 30+ countries worldwide." },
  { icon: RiLightbulbLine, title: "Cutting-Edge Insights", desc: "Get ahead with actionable strategies, trends, and breakthrough ideas." },
  { icon: RiShakeHandsLine, title: "Exclusive Partnerships", desc: "Close deals, forge alliances, and open doors you never knew existed." },
  { icon: RiAwardLine, title: "Certified Excellence", desc: "Earn recognized credentials and showcase your leadership journey." },
  { icon: RiRocketLine, title: "Career Acceleration", desc: "Transform your trajectory with mentorship, visibility, and opportunity." },
];

/* ─── THEME — Extracted from Meeting21 Video ──────────────────────────────
   Primary Teal  : #2A9D8F  (navbar active, pills, icons, borders, accents)
   Primary Dark  : #1F7A6E  (hover states)
   Primary Light : #3BBCAE  (gradient end, light tints)
   Gold/Amber    : #D4A017  (CTA buttons, ticker bar, "10+" badge, "21" text)
   Gold Dark     : #B8880F  (gold hover)
   Gold Light    : #E8B520  (gradient end)
   Navy Deep     : #0D2B3E  (navbar bg, hero overlay, dark sections)
   Navy          : #1A3A4A  (secondary dark bg, newsletter bg)
   Text Dark     : #1C3461  (headings — dark navy-blue)
   Text Mid      : #2D4A5A  (body text)
   Text Muted    : #6B8090  (secondary / hint text)
   Border        : #C8E8E4  (teal-tinted border)
   Border Light  : #E2F2F0  (very light teal border)
   Soft Teal     : #E8F5F3  (icon backgrounds, chip backgrounds)
   Off-white     : #F4F9F8  (section alternating background)
   White         : #FFFFFF
────────────────────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
};

/* ─── ANIMATION VARIANTS ─────────────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] } }),
};
const fadeScale = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: (d = 0) => ({ opacity: 1, scale: 1, transition: { duration: 0.5, delay: d, ease: [0.22, 1, 0.36, 1] } }),
};

/* ─── GLOBAL CSS ──────────────────────────────────────────────────────────── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  body { background: #FFFFFF; color: #1C3461; font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #F4F9F8; }
  ::-webkit-scrollbar-thumb { background: #2A9D8F; border-radius: 8px; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse-dot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.85); } }
  .dot-bg { background-image: radial-gradient(circle, #B8DDD9 1px, transparent 1px); background-size: 28px 28px; }
  .stripe-bg { background-image: repeating-linear-gradient(-45deg, transparent, transparent 6px, rgba(42,157,143,0.03) 6px, rgba(42,157,143,0.03) 12px); }
  .lift-card { transition: transform 0.28s ease, box-shadow 0.28s ease; }
  .lift-card:hover { transform: translateY(-6px); box-shadow: 0 20px 56px rgba(42,157,143,0.16) !important; }
  .service-card { position: relative; border-radius: 14px; overflow: hidden; cursor: pointer; transition: box-shadow 0.3s; }
  .service-card img { width: 100%; height: 210px; object-fit: cover; display: block; transition: transform 0.55s ease; }
  .service-card:hover img { transform: scale(1.06); }
  .service-card:hover { box-shadow: 0 20px 56px rgba(42,157,143,0.20); }
  .section-rule { width: 56px; height: 3px; background: linear-gradient(90deg, #2A9D8F, #3BBCAE); border-radius: 2px; }
  .pill { display: inline-flex; align-items: center; gap: 6px; background: rgba(42,157,143,0.10); color: #2A9D8F; font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; padding: 5px 14px; border-radius: 100px; border: 1px solid rgba(42,157,143,0.20); }
  .form-input { width: 100%; padding: 13px 16px; border-radius: 8px; font-size: 14px; border: 1.5px solid #C8E8E4; outline: none; color: #1C3461; background: #FFFFFF; font-family: 'DM Sans', sans-serif; transition: border-color 0.2s, box-shadow 0.2s; }
  .form-input:focus { border-color: #2A9D8F; box-shadow: 0 0 0 3px rgba(42,157,143,0.14); }
  .form-input::placeholder { color: #9BBBB7; }
  .pricing-card { transition: transform 0.3s, box-shadow 0.3s; }
  .pricing-card:hover { transform: translateY(-10px); }
  .stat-number { font-variant-numeric: tabular-nums; }
  .sponsor-badge { background: #FFFFFF; border: 1.5px solid #C8E8E4; border-radius: 10px; padding: 14px 36px; display: flex; align-items: center; justify-content: center; transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s; white-space: nowrap; min-width: 90px; }
  .sponsor-badge:hover { border-color: #2A9D8F; box-shadow: 0 8px 28px rgba(42,157,143,0.14); transform: translateY(-3px); }
`;

/* ─── SERVICE CARD ───────────────────────────────────────────────────────── */
const ServiceCard = ({ svc, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp} custom={i * 0.08} className="service-card" style={{ boxShadow: T.shadow, border: `1.5px solid ${T.border}`, background: "#FFFFFF" }}>
      <div style={{ position: "relative" }}>
        <img src={svc.img} alt={svc.title} style={{ width: "100%", height: 200, objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(13,43,62,0.78) 0%, transparent 55%)" }} />
        <div style={{ position: "absolute", bottom: 16, left: 16, right: 16 }}>
          <div style={{ width: 38, height: 38, background: T.primary, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
            <svc.icon size={18} style={{ color: "#fff" }} />
          </div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#fff" }}>{svc.title}</div>
        </div>
      </div>
      <div style={{ background: "#FFFFFF", padding: "18px 20px", borderTop: `3px solid ${T.primary}` }}>
        <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.7 }}>{svc.desc}</p>
      </div>
    </motion.div>
  );
};

/* ─── SCROLLING ANNOUNCEMENT BANNER (Gold — matches video) ───────────────── */
const ScrollingAnnouncementBanner = () => {
  const [apiAnnouncements, setApiAnnouncements] = useState(null);
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/announcements/active`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.success && data.announcement?.isActive) setApiAnnouncements([data.announcement.message]);
      } catch {}
    };
    fetchAnnouncements();
  }, []);
  const getCurrentDate = () => new Date().toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
  const items = apiAnnouncements || tickerAnnouncements;
  const repeated = Array(6).fill(items).flat(); // Reduced from 10 to 6 since animation is slower
  return (
    <>
      <style>{`
        @keyframes ticker-scroll { 
          from { transform: translateX(0); } 
          to { transform: translateX(-50%); } 
        }
        @keyframes pulse-dot2 { 
          0% { opacity:0.6; transform:scale(1); } 
          50% { opacity:1; transform:scale(1.3); } 
          100% { opacity:0.6; transform:scale(1); } 
        }
        .ticker-track { 
          display: inline-flex; 
          align-items: center; 
          width: max-content; 
          animation: ticker-scroll 60s linear infinite; /* Changed from 35s to 60s - slower */
          white-space: nowrap; 
        }
        .ticker-track:hover {
          animation-play-state: paused; /* Pause on hover for better readability */
        }
      `}</style>
      <div style={{ 
        background: T.gradGold, 
        color: T.navyDeep, 
        height: 56, // Increased from 42 to 56
        display: "flex", 
        alignItems: "center", 
        overflow: "hidden", 
        position: "relative", 
        zIndex: 10000 
      }}>
        <div style={{ 
          flexShrink: 0, 
          display: "flex", 
          alignItems: "center", 
          gap: 10, // Increased from 8
          padding: "0 20px", // Increased from 16px
          height: "100%", 
          background: "rgba(0,0,0,0.12)", 
          borderRight: "1px solid rgba(0,0,0,0.10)", 
          whiteSpace: "nowrap", 
          fontSize: 12, // Increased from 10
          fontWeight: 800, 
          letterSpacing: "0.16em", 
          zIndex: 2, 
          color: T.navyDeep 
        }}>
          <span style={{ 
            width: 8, // Increased from 6
            height: 8, // Increased from 6
            borderRadius: "50%", 
            background: T.navyDeep, 
            display: "inline-block", 
            animation: "pulse-dot2 1.4s ease infinite" 
          }} />
          LIVE UPDATES
        </div>
        <div style={{ overflow: "hidden", flex: 1 }}>
          <div className="ticker-track">
            {repeated.map((item, i) => (
              <span key={i} style={{ 
                display: "inline-flex", 
                alignItems: "center", 
                whiteSpace: "nowrap", 
                fontSize: 14, // Increased from 13
                fontWeight: 700, 
                color: T.navyDeep, 
                padding: "0 16px" // Increased from 10px
              }}>
                <span>📅 {getCurrentDate()}</span>
                <span style={{ margin: "0 12px", opacity: 0.4 }}>|</span> {/* Increased margin from 10px */}
                <span>{item}</span>
                <span style={{ margin: "0 12px", opacity: 0.4 }}>|</span> {/* Increased margin from 10px */}
              </span>
            ))}
          </div>
        </div>
        <div style={{ 
          position: "absolute", 
          right: 0, 
          top: 0, 
          width: 80, // Increased from 70
          height: "100%", 
          background: `linear-gradient(to right, transparent, ${T.accentGoldDark})`, 
          pointerEvents: "none" 
        }} />
      </div>
    </>
  );
};

/* ─── SECTION HEADER ─────────────────────────────────────────────────────── */
function SectionHeader({ eyebrow, title, description, light = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <div ref={ref} style={{ textAlign: "center", marginBottom: 64 }}>
      <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeScale} custom={0} className="pill"
        style={{ marginBottom: 18, display: "inline-flex", background: light ? "rgba(255,255,255,0.12)" : undefined, color: light ? "#FFFFFF" : undefined, borderColor: light ? "rgba(255,255,255,0.2)" : undefined }}>
        {eyebrow}
      </motion.div>
      <motion.h2 initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp} custom={0.08}
        style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(32px, 4vw, 50px)", fontWeight: 700, color: light ? "#FFFFFF" : T.text, lineHeight: 1.18, letterSpacing: "-0.02em", marginBottom: 14 }}>
        {title}
      </motion.h2>
      <motion.div initial={{ scaleX: 0 }} animate={inView ? { scaleX: 1 } : {}} transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: 56, height: 3, background: light ? "rgba(255,255,255,0.5)" : T.gradPrimary, borderRadius: 2, margin: "0 auto 18px", transformOrigin: "left" }} />
      {description && (
        <motion.p initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp} custom={0.18}
          style={{ color: light ? "rgba(255,255,255,0.72)" : T.textMuted, fontSize: 17, maxWidth: 580, margin: "0 auto", lineHeight: 1.75, fontWeight: 400 }}>
          {description}
        </motion.p>
      )}
    </div>
  );
}

/* ─── VALUE CARD ─────────────────────────────────────────────────────────── */
function ValueCard({ value, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const IconComponent = value.icon;
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 28 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: index * 0.1 }} className="lift-card"
      style={{ background: "#FFFFFF", borderRadius: 16, border: `1.5px solid ${T.border}`, boxShadow: T.shadow, padding: "36px 28px", textAlign: "center", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: T.gradPrimary }} />
      <div style={{ width: 68, height: 68, borderRadius: 16, background: T.softTeal, border: `1.5px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px" }}>
        <IconComponent size={30} color={T.primary} />
      </div>
      <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 21, fontWeight: 700, color: T.text, marginBottom: 12, letterSpacing: "-0.01em" }}>{value.title}</h3>
      <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.7 }}>{value.description}</p>
    </motion.div>
  );
}

/* ─── STAT CARD ──────────────────────────────────────────────────────────── */
function StatCard({ icon: Icon, value, label, description, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [count, setCount] = useState(0);
  const rawValue = parseInt(value) || 0;
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = rawValue / (1200 / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= rawValue) { setCount(rawValue); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, rawValue]);
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeScale} custom={delay} className="lift-card"
      style={{ textAlign: "center", padding: "40px 24px", background: "#FFFFFF", borderRadius: 16, boxShadow: T.shadow, border: `1.5px solid ${T.border}`, position: "relative", overflow: "hidden", cursor: "default" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: T.gradPrimary }} />
      <div style={{ width: 60, height: 60, background: T.softTeal, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
        <Icon style={{ fontSize: 26, color: T.primary }} />
      </div>
      <div className="stat-number" style={{ fontFamily: "'Playfair Display', serif", fontSize: 50, fontWeight: 700, color: T.text, lineHeight: 1, marginBottom: 8, letterSpacing: "-0.02em" }}>
        {value.includes("+") ? `${count}+` : count}
      </div>
      <div style={{ color: T.primary, fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      {description && <div style={{ color: T.textMuted, fontSize: 13, fontWeight: 500 }}>{description}</div>}
    </motion.div>
  );
}

/* ─── TESTIMONIAL CARD ───────────────────────────────────────────────────── */
function TestimonialCard({ t, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp} custom={delay} className="lift-card"
      style={{ background: "#FFFFFF", border: `1.5px solid ${T.border}`, borderRadius: 16, padding: "36px 30px", boxShadow: T.shadow, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -8, right: 20, fontSize: 90, opacity: 0.05, fontFamily: "'Playfair Display', serif", color: T.primary, pointerEvents: "none", lineHeight: 1 }}>❝</div>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: T.gradPrimary, borderRadius: "16px 0 0 16px" }} />
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {[...Array(5)].map((_, i) => <RiStarFill key={i} style={{ color: T.accentGold, fontSize: 15 }} />)}
      </div>
      <p style={{ fontSize: 15, color: T.textMid, lineHeight: 1.8, marginBottom: 28, fontStyle: "italic" }}>"{t.text}"</p>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <img src={t.img} alt={t.name} style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover", border: `2px solid ${T.border}`, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: "'Playfair Display', serif" }}>{t.name}</div>
          <div style={{ fontSize: 13, color: T.primary, fontWeight: 600 }}>{t.role}</div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── PRICING CARD ───────────────────────────────────────────────────────── */
function PricingCard({ tier, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeScale} custom={index * 0.12} className="pricing-card"
      style={{ background: tier.featured ? T.navy : "#FFFFFF", border: tier.featured ? "none" : `1.5px solid ${T.border}`, borderRadius: 18, overflow: "hidden", boxShadow: tier.featured ? "0 24px 64px rgba(13,43,62,0.30)" : T.shadow, position: "relative" }}>
      {tier.featured && (
        <div style={{ background: T.gradGold, color: T.navyDeep, textAlign: "center", padding: "8px", fontSize: 10, fontWeight: 800, letterSpacing: "0.14em" }}>★ MOST POPULAR</div>
      )}
      <div style={{ padding: "32px 30px 36px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: tier.featured ? "rgba(255,255,255,0.6)" : T.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>{tier.title}</div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 54, fontWeight: 700, color: tier.featured ? "#FFFFFF" : T.primary, lineHeight: 1, marginBottom: 8, letterSpacing: "-0.02em" }}>
          {tier.priceDisplay || `$${tier.price}`}
        </div>
        <div style={{ height: 1, background: tier.featured ? "rgba(255,255,255,0.1)" : T.borderLight, margin: "24px 0" }} />
        <div style={{ marginBottom: 32 }}>
          {tier.features && tier.features.map((f, fi) => (
            <div key={fi} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: tier.featured ? "rgba(42,157,143,0.25)" : T.softTeal, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <RiCheckLine size={13} style={{ color: tier.featured ? "#3BBCAE" : T.primary }} />
              </div>
              <span style={{ fontSize: 14, color: tier.featured ? "rgba(255,255,255,0.8)" : T.textMid, fontWeight: 500 }}>{f}</span>
            </div>
          ))}
        </div>
        <Link to="/events" state={{ selectedTier: tier }}
          style={{ display: "block", textAlign: "center", background: tier.featured ? T.gradGold : T.gradPrimary, color: tier.featured ? T.navyDeep : "#FFFFFF", padding: "14px", borderRadius: 10, fontSize: 15, fontWeight: 700, textDecoration: "none", letterSpacing: "0.02em", boxShadow: tier.featured ? "0 8px 24px rgba(212,160,23,0.35)" : "0 8px 24px rgba(42,157,143,0.28)", transition: "opacity 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
          Register Now
        </Link>
      </div>
    </motion.div>
  );
}

/* ─── SPONSOR CARD ───────────────────────────────────────────────────────── */
function SponsorCard({ sponsor }) {
  const [imgError, setImgError] = useState(false);
  const showLogo = sponsor.logoUrl && !imgError;
  return (
    <div className="sponsor-badge">
      {showLogo ? (
        <img src={sponsor.logoUrl} alt={sponsor.name} onError={() => setImgError(true)} style={{ height: 38, maxWidth: 120, objectFit: "contain", display: "block" }} />
      ) : (
        <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", color: T.textMid }}>{sponsor.name}</span>
      )}
    </div>
  );
}

/* ─── MAIN HOME COMPONENT ────────────────────────────────────────────────── */
const Home = ({ Navbar }) => {
  const [heroSlides, setHeroSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "" });
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const slidesLenRef = useRef(0);

  useEffect(() => { fetchPricing(); }, []);
  const fetchPricing = async () => {
    try {
      setPricingLoading(true);
      const response = await axios.get(`${API_BASE}/api/pricing`);
      if (response.data.success && response.data.data.length > 0) setPricingTiers(response.data.data);
      else setPricingTiers(getDefaultPricing());
    } catch { setPricingTiers(getDefaultPricing()); }
    finally { setPricingLoading(false); }
  };

  const getDefaultPricing = () => [
    { _id: "1", title: "Student Delegate", price: 299, priceDisplay: "$299", featured: false, features: ["Welcome coffee", "Access to All Talks and Posters", "Conference material", "Lunch, tea/coffee breaks", "WiFi in meeting rooms"] },
    { _id: "2", title: "Oral Talk", price: 699, priceDisplay: "$699", featured: true, features: ["Welcome coffee", "Access to All Talks and Posters", "Conference material", "Lunch, tea/coffee breaks", "WiFi in meeting rooms", "Certificate of Presentation"] },
    { _id: "3", title: "Delegate / Listener", price: 799, priceDisplay: "$799", featured: false, features: ["Welcome coffee", "Access to All Talks and Posters", "Conference material", "Lunch, tea/coffee breaks", "WiFi in meeting rooms", "Networking Dinner"] },
  ];

  useEffect(() => { slidesLenRef.current = heroSlides.length; }, [heroSlides]);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/hero-slides`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setHeroSlides(data.map(s => ({ ...s, image: s.image?.startsWith("http") ? s.image : `${API_BASE}${s.image}` })));
          setCurrentSlide(0);
        }
      } catch {}
    };
    fetchSlides();
  }, []);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (heroSlides.length > 1) timerRef.current = setInterval(() => setCurrentSlide(prev => (prev + 1) % slidesLenRef.current), 5500);
    return () => clearInterval(timerRef.current);
  }, [heroSlides]);

  const goToSlide = n => {
    setCurrentSlide(n);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCurrentSlide(prev => (prev + 1) % slidesLenRef.current), 5500);
  };

  const handleChange = e => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubscribe = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) { alert("Please fill all fields"); return; }
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/subscribe`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(formData) });
      const data = await response.json();
      if (response.ok) { alert("✅ Subscription successful! Thank you."); setFormData({ firstName: "", lastName: "", email: "" }); }
      else alert(data.message || "Subscription failed");
    } catch { alert("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const slide = heroSlides[currentSlide] || { title: "", image: "" };

  return (
    <div style={{ background: "#FFFFFF", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{GLOBAL_CSS}</style>
      {Navbar && <Navbar />}


{/* ══════ HERO ══════ */}
<section style={{ position: "relative", height: "calc(100vh - 68px)", overflow: "hidden", minHeight: 560 }}>
  <AnimatePresence mode="wait">
    {heroSlides.length > 0 && (
      <motion.div 
        key={`slide-${currentSlide}`} 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        transition={{ duration: 0.7, ease: "easeInOut" }} 
        style={{ position: "absolute", inset: 0, zIndex: 0 }}
      >
        <img src={slide.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(110deg, rgba(13,43,62,0.90) 0%, rgba(26,58,74,0.74) 50%, rgba(42,157,143,0.22) 100%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(13,43,62,0.65) 0%, transparent 50%)", pointerEvents: "none" }} />

        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "flex-start", padding: "110px 72px 80px", maxWidth: 1000 }}>
          {/* Subtitle/Badge - with dynamic styles from backend */}
          {slide.subtitle && (
            <motion.div 
              initial={{ opacity: 0, y: 16 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.5, delay: 0.05 }}
              style={{ 
                display: "inline-flex", 
                alignItems: "center", 
                gap: 8, 
                background: "rgba(42,157,143,0.18)", 
                backdropFilter: "blur(8px)", 
                border: "1px solid rgba(42,157,143,0.35)", 
                borderRadius: 100, 
                padding: "6px 16px", 
                marginBottom: 24, 
                width: "fit-content",
                // Apply dynamic styles from backend
                fontFamily: slide.textStyles?.subtitleFontFamily || "'DM Sans', sans-serif",
                fontSize: slide.textStyles?.subtitleFontSize || "11.5px",
                fontWeight: slide.textStyles?.subtitleFontWeight || "700",
                color: slide.textStyles?.subtitleColor || "rgba(255,255,255,0.95)",
                letterSpacing: "0.12em",
                textTransform: "uppercase"
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.accentGold, display: "inline-block", animation: "pulse-dot 1.4s ease infinite" }} />
              {slide.subtitle}
            </motion.div>
          )}

          {/* Main Title - with dynamic styles from backend */}
          <motion.h1 
            initial={{ opacity: 0, y: 28 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.55, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            style={{ 
              // Apply dynamic styles from backend
              fontFamily: slide.textStyles?.titleFontFamily || "'Playfair Display', serif",
              fontSize: slide.textStyles?.titleFontSize || "clamp(40px, 6vw, 82px)",
              fontWeight: slide.textStyles?.titleFontWeight || "700",
              lineHeight: slide.textStyles?.titleLineHeight || "1.06",
              letterSpacing: slide.textStyles?.titleLetterSpacing || "-0.02em",
              color: slide.textStyles?.titleColor || "#FFFFFF",
              marginBottom: 32, 
              textShadow: "0 2px 24px rgba(0,0,0,0.3)", 
              whiteSpace: "pre-line", 
              maxWidth: 720 
            }}
          >
            {slide.title}
          </motion.h1>

          {/* CTA Button */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5, delay: 0.28 }} 
            style={{ display: "flex", gap: 14, flexWrap: "wrap" }}
          >
            <Link 
              to={slide.ctaLink || "/Events"}
              style={{ 
                display: "inline-flex", 
                alignItems: "center", 
                gap: 10, 
                background: T.gradGold, 
                color: T.navyDeep, 
                padding: "14px 30px", 
                borderRadius: 10, 
                fontSize: 15, 
                fontWeight: 700, 
                textDecoration: "none", 
                boxShadow: "0 8px 32px rgba(212,160,23,0.40)", 
                transition: "all 0.25s ease", 
                letterSpacing: "0.01em" 
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(212,160,23,0.50)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(212,160,23,0.40)"; }}
            >
              <RiCalendarLine size={18} />
              {slide.ctaText || "Upcoming Events"}
            </Link>
          </motion.div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>

  {/* Slide navigation dots and arrows remain the same */}
  {heroSlides.length > 1 && (
    <div style={{ position: "absolute", bottom: 36, left: 72, zIndex: 3, display: "flex", gap: 8, alignItems: "center" }}>
      {heroSlides.map((_, i) => (
        <motion.button 
          key={i} 
          onClick={() => goToSlide(i)}
          animate={{ width: i === currentSlide ? 36 : 8, background: i === currentSlide ? T.accentGold : "rgba(255,255,255,0.35)" }}
          transition={{ duration: 0.3 }}
          style={{ height: 4, borderRadius: 2, border: "none", cursor: "pointer", padding: 0 }} 
        />
      ))}
    </div>
  )}

  {heroSlides.length > 1 && [
    { dir: "left", onClick: () => goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length), icon: RiArrowLeftSLine },
    { dir: "right", onClick: () => goToSlide((currentSlide + 1) % heroSlides.length), icon: RiArrowRightSLine },
  ].map(({ dir, onClick, icon: Icon }) => (
    <button 
      key={dir} 
      onClick={onClick}
      style={{ 
        position: "absolute", 
        [dir]: 24, 
        top: "50%", 
        transform: "translateY(-50%)", 
        zIndex: 3, 
        width: 48, 
        height: 48, 
        borderRadius: "50%", 
        background: "rgba(255,255,255,0.12)", 
        backdropFilter: "blur(8px)", 
        border: "1.5px solid rgba(255,255,255,0.25)", 
        color: "#fff", 
        cursor: "pointer", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        transition: "background 0.2s" 
      }}
      onMouseEnter={e => e.currentTarget.style.background = "rgba(42,157,143,0.40)"}
      onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
    >
      <Icon size={24} />
    </button>
  ))}
</section>

      <ScrollingAnnouncementBanner />

      {/* ══════ ABOUT SECTION ══════ */}
      <section style={{ padding: "96px 48px", background: T.offWhite, borderBottom: `1px solid ${T.borderLight}` }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <SectionHeader eyebrow="About Meetings21" title="Where Global Leaders Shape Tomorrow" description="A premier platform connecting visionaries, policymakers, and innovators from across the globe." />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center", marginBottom: 72 }}>
            <div style={{ position: "relative" }}>
              <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
                <img src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=700&q=80" alt="Summit" style={{ width: "100%", height: 360, objectFit: "cover", borderRadius: 16, display: "block", boxShadow: T.shadowMd }} />
                <img src="https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=400&q=80" alt="Conference" style={{ width: "54%", height: 190, objectFit: "cover", borderRadius: 14, display: "block", boxShadow: T.shadowMd, position: "absolute", bottom: -40, right: -28, border: "4px solid #FFFFFF" }} />
              </motion.div>
              <div style={{ position: "absolute", top: 24, left: 24, background: T.gradGold, color: T.navyDeep, borderRadius: 14, padding: "14px 20px", boxShadow: "0 12px 36px rgba(212,160,23,0.35)", zIndex: 2 }}>
                <div style={{ fontSize: 30, fontFamily: "'Playfair Display', serif", fontWeight: 700, lineHeight: 1 }}>10+</div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", opacity: 0.75, marginTop: 4 }}>YEARS OF EXCELLENCE</div>
              </div>
            </div>

            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <p style={{ color: T.textMid, fontSize: 16, lineHeight: 1.8, marginBottom: 20, fontWeight: 400 }}>
                We understand the importance of academic conferences in the 21st century to foster collaboration, share knowledge, and advance innovation. We curate conferences with a deep appreciation for the scholarly community.
              </p>
              <p style={{ color: T.textMid, fontSize: 16, lineHeight: 1.8, marginBottom: 36, fontWeight: 400 }}>
                Each conference is designed to surpass expectations by our team of experienced professionals. We handle every detail with precision so you can focus on what really matters — the content and connections that make academic conferences meaningful.
              </p>
              <Link to="/about"
                style={{ background: T.gradGold, color: T.navyDeep, padding: "13px 30px", borderRadius: 10, fontSize: 15, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 10, boxShadow: "0 8px 28px rgba(212,160,23,0.30)", transition: "opacity 0.2s, transform 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                Know More <RiArrowRightLine />
              </Link>
            </motion.div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
            {["https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600&q=80", "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=600&q=80", "https://images.unsplash.com/photo-1559223607-a43c990c692c?w=600&q=80"].map((src, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} style={{ borderRadius: 14, overflow: "hidden", position: "relative" }}>
                <img src={src} alt="Event" style={{ width: "100%", height: 210, objectFit: "cover", display: "block", transition: "transform 0.5s ease" }} onMouseEnter={e => e.currentTarget.style.transform = "scale(1.04)"} onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(13,43,62,0.35) 0%, transparent 60%)" }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ CORE PHILOSOPHY SECTION ══════ */}
      <section style={{ padding: "96px 48px", background: "linear-gradient(135deg, #FFFFFF 0%, #F0F8F6 100%)", borderTop: `1px solid ${T.borderLight}`, borderBottom: `1px solid ${T.borderLight}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
          <SectionHeader eyebrow="Our Philosophy" title="Core Values That Drive Us" description="Passion for academic excellence drives everything we do." />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24 }}>
            {values.map((value, index) => <ValueCard key={index} value={value} index={index} />)}
          </div>
        </div>
      </section>

      {/* ══════ SERVICES SECTION with dot-bg ══════ */}
      <section style={{ padding: "96px 48px", background: T.navyDeep, borderBottom: `1px solid ${T.borderLight}` }} className="dot-bg">
        <SectionHeader eyebrow="Why Meetings21?" title="Our Services &amp; Highlights" description="Everything you need for a world-class conference experience, all under one roof." light={true} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24, maxWidth: 1200, margin: "0 auto" }}>
          {services.map((svc, i) => <ServiceCard key={i} svc={svc} i={i} />)}
        </div>
      </section>

      {/* ══════ WHY ATTEND SECTION ══════ */}
      <section style={{ padding: "96px 48px", background: T.offWhite, borderTop: `1px solid ${T.borderLight}`, borderBottom: `1px solid ${T.borderLight}` }} className="stripe-bg">
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center" }}>
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 12, height: 450 }}>
            <img src="https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=500&q=80" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 14, gridRow: "1 / 3", boxShadow: T.shadow }} />
            <img src="https://images.unsplash.com/photo-1559223607-a43c990c692c?w=400&q=80" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 14, boxShadow: T.shadow }} />
            <img src="https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=400&q=80" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 14, boxShadow: T.shadow }} />
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <span className="pill" style={{ marginBottom: 20, display: "inline-flex" }}>Why Attend?</span>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(26px, 3.5vw, 42px)", fontWeight: 700, color: T.text, lineHeight: 1.2, marginBottom: 16, letterSpacing: "-0.02em" }}>
              Why You Should Join Our Event?
            </h2>
            <div className="section-rule" style={{ marginBottom: 24 }} />
            <p style={{ color: T.textMuted, fontSize: 15, lineHeight: 1.8, marginBottom: 32 }}>
              Our summits deliver unparalleled knowledge, world-class networking, and career-defining opportunities — all in one extraordinary venue.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {whyAttend.map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.07 }} className="lift-card"
                  style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "16px 18px", background: "#FFFFFF", borderRadius: 12, border: `1.5px solid ${T.border}`, boxShadow: "0 2px 10px rgba(42,157,143,0.06)", cursor: "pointer" }}>
                  <div style={{ width: 44, height: 44, background: T.softTeal, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1.5px solid ${T.border}` }}>
                    <item.icon size={20} style={{ color: T.primary }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4, fontFamily: "'Playfair Display', serif" }}>{item.title}</div>
                    <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>{item.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════ PRICING SECTION with dot-bg (same as services) ══════ */}
      <section style={{ padding: "96px 48px", background: T.navyDeep, borderTop: `1px solid ${T.borderLight}`, borderBottom: `1px solid ${T.borderLight}` }} className="dot-bg">
        <SectionHeader eyebrow="Ticket Pricing" title="Explore Pricing Policies" description="Choose the registration tier that suits your role and unlock a world of opportunity." light={true} />
        {pricingLoading ? (
          <div style={{ display: "flex", justifyContent: "center", minHeight: 300, alignItems: "center" }}>
            <div style={{ width: 44, height: 44, border: `3px solid ${T.borderLight}`, borderTopColor: T.primary, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 28, maxWidth: 1000, margin: "0 auto" }}>
            {pricingTiers.map((tier, index) => <PricingCard key={tier._id || index} tier={tier} index={index} />)}
          </div>
        )}
      </section>

      {/* ══════ NEWSLETTER SECTION - REMOVED BACKGROUND, NOW WHITE ══════ */}
      <section style={{ padding: "96px 48px", background: "#FFFFFF" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", background: T.gradNavy, borderRadius: 24, padding: "72px 72px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center", position: "relative", overflow: "hidden", boxShadow: T.shadowLg }}>
          <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, borderRadius: "50%", background: "rgba(42,157,143,0.08)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -60, left: 200, width: 200, height: 200, borderRadius: "50%", background: "rgba(212,160,23,0.07)", pointerEvents: "none" }} />

          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(42,157,143,0.15)", border: "1px solid rgba(42,157,143,0.25)", borderRadius: 100, padding: "5px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: T.primaryLight, textTransform: "uppercase", marginBottom: 20 }}>
              Newsletter Sign-Up
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(24px, 3vw, 38px)", fontWeight: 700, color: "#FFFFFF", marginBottom: 16, lineHeight: 1.22, letterSpacing: "-0.02em" }}>
              Subscribe to Get Latest News
            </h2>
            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 15, lineHeight: 1.75, marginBottom: 12 }}>
              Stay ahead with speaker announcements, agenda updates, early-bird offers, and summit highlights delivered to your inbox.
            </p>
            <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12 }}>* We never share your personal info with anyone.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div style={{ background: "rgba(255,255,255,0.06)", border: "1.5px solid rgba(42,157,143,0.18)", borderRadius: 16, padding: "36px 32px", backdropFilter: "blur(12px)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", gap: 12 }}>
                  <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} className="form-input" style={{ background: "rgba(255,255,255,0.95)", flex: 1 }} />
                  <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} className="form-input" style={{ background: "rgba(255,255,255,0.95)", flex: 1 }} />
                </div>
                <input type="email" name="email" placeholder="Enter your email address" value={formData.email} onChange={handleChange} className="form-input" style={{ background: "rgba(255,255,255,0.95)" }} />
                <button onClick={handleSubscribe} disabled={loading}
                  style={{ background: T.gradGold, color: T.navyDeep, padding: "14px 24px", borderRadius: 10, fontSize: 15, fontWeight: 700, border: "none", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, opacity: loading ? 0.7 : 1, transition: "opacity 0.2s, transform 0.2s", boxShadow: "0 4px 16px rgba(212,160,23,0.35)" }}
                  onMouseEnter={e => !loading && (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                  <RiMailLine size={18} />
                  {loading ? "Subscribing…" : "Subscribe Now"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;